import React, { useEffect, useState, useRef } from 'react';
import { Container, PageHeader } from '../layout';
import { Button } from '../layout/buttons';
import { Col, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import CanvassInfo from './CanvassInfo';
import Icon from '../layout/icons/Icons';
import CanvassCreationFlow from './CanvassCreationFlow';
import styles from './NewCanvass.module.css';

const NewCanvass = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const hasReadSessionRef = useRef(false);

    // Initialize state synchronously from sessionStorage to prevent undefined on initial render
    const getInitialState = () => {
        try {
            const sessionState = sessionStorage.getItem('newCanvassState');
            if (sessionState) {
                const parsed = JSON.parse(sessionState);
                sessionStorage.removeItem('newCanvassState'); // Clean up immediately
                return parsed;
            }
        } catch (e) {
            console.error('Failed to parse sessionStorage state:', e);
        }
        return {};
    };

    const [state, setState] = useState(getInitialState);
    const [currentStep, setCurrentStep] = useState('context'); // 'context' | 'details' | 'setup'
    const [selectedContext, setSelectedContext] = useState(null);
    const [interviewStarted, setInterviewStarted] = useState(false);

    useEffect(() => {
        // Mark as read to prevent re-reading on re-renders
        hasReadSessionRef.current = true;
    }, []);

    const title = state.title || 'New Canvass';
    const description = state.description || "Let's get started by providing a title and description for your canvass.";
    const canvassBrief = state.canvassBrief || '';

    const handleContextSelect = (context) => {
        setSelectedContext(context);
        setCurrentStep('details');
        
        // Update state with context information
        setState(prev => ({
            ...prev,
            context: context,
            // Only update title and description if user hasn't provided them
            title: prev.title && prev.title.trim() !== '' 
                ? prev.title 
                : context.type === 'company' 
                ? `Canvass for ${context.contextData?.name || 'Organization'}`
                : context.type === 'stakeholder'
                ? `Canvass for ${context.contextData?.name || 'Stakeholder'}`
                : 'Freeform Canvass',
            canvassBrief: prev.canvassBrief && prev.canvassBrief.trim() !== ''
                ? prev.canvassBrief
                : context.type === 'company'
                ? `Creating a canvass for ${context.contextData?.name || 'your organization'}`
                : context.type === 'stakeholder'
                ? `Creating a canvass for ${context.contextData?.name || 'your stakeholder'}`
                : 'Creating a freeform canvass with full flexibility'
        }));
    };

    const handleProceedToDetails = () => {
        setCurrentStep('details');
    };

    const handleBackToContext = () => {
        setCurrentStep('context');
        setSelectedContext(null);
    };

    return (
        <Container fluid>
            {currentStep === 'context' ? (
                // Context Selection Step
                <CanvassCreationFlow 
                    onContextSelect={handleContextSelect}
                    onProceed={handleProceedToDetails}
                />
            ) : (
                // Details and Setup Steps (original flow)
                <>
                    <Row>
                        <Col className="d-flex justify-content-start">
                            <a className={styles.backLink} href="/canvasses" aria-label="Back to Canvasses">
                                <Icon className={styles.backIcon} name="arrow/arrow_back" color="var(--brand-as-blue-600)" width={22} height={22} />
                                <div className={styles.backText}>Back to Canvasses</div>
                            </a>
                        </Col>
                    </Row>
                    <Row className="align-items-center">
                        <Col md={9}>
                            <div className="d-flex align-items-center">
                                <PageHeader title={title} subtext={canvassBrief} align="left" size="lg" className="text-primary mb-0"/>
                                {selectedContext && (
                                    <span className="badge bg-secondary text-dark ms-3">
                                        {selectedContext.type === 'company' 
                                            ? `Organization: ${selectedContext.contextData?.name || 'Selected Organization'}`
                                            : selectedContext.type === 'stakeholder'
                                            ? `Stakeholder: ${selectedContext.contextData?.name || 'Selected Stakeholder'}`
                                            : 'Freeform Canvass'
                                        }
                                    </span>
                                )}
                            </div>
                        </Col>
                        <Col md={3} className="d-flex justify-content-end">
                            {selectedContext && !interviewStarted && (
                                <Button 
                                    variant="outline-neutral" 
                                    onClick={handleBackToContext}
                                    className="me-2"
                                    title="Change context"
                                >
                                    Change Context
                                </Button>
                            )}
                            <Button textColor="var(--brand-as-neutral-black-950)" color="var(--brand-as-neutral-black-transparent-05)" text="Finish Later" >
                                Finish Later
                            </Button>
                        </Col>
                    </Row>
                    <CanvassInfo
                        canvass={{ status: 'in_progress', timestamp: new Date().toISOString() }}
                        clientId={state.clientId}
                        displayName={state.displayName}
                        canvassName={title}
                        canvassBrief={canvassBrief}
                        context={selectedContext}
                        interviewStarted={interviewStarted}
                        onInterviewStart={() => setInterviewStarted(true)}
                    />
                </>
            )}
        </Container>
    );
};

export default NewCanvass;
